import { lazy, useEffect } from "react";
import { useSelector } from "react-redux";
import { Route, Routes, useLocation } from "react-router-dom";
import { useGetDataQuery } from "../redux/Apis/General";
import HeartRateCalculator from "../screens/calculators/heartRate";
import Hydration from "../screens/calculators/hydration";
import IdealBodyWeightCalculator from "../screens/calculators/idealbodyweight";
import OneRepMax from "../screens/calculators/orm";
import Sleep from "../screens/calculators/sleep";
import WaistToHipRatio from "../screens/calculators/whr";
import CommunityEdit from "../screens/communityEdit";
import Error from "../screens/Error";
import FitnessTest from "../screens/fitnesstest";
import BalanceTest from "../screens/fitnesstest/balance";
import CardioFitnessTest from "../screens/fitnesstest/cardio";
import FlexibilityTest from "../screens/fitnesstest/flexibility";
import StrengthTest from "../screens/fitnesstest/strength";
import Home from "../screens/home";
import InstructorFollower from "../screens/instructordetails/InstructorFollower";
import InstructorReview from "../screens/instructordetails/InstructorReview";
import LearnerDetails from "../screens/learnerdetails";
import PrivateMembership from "../screens/membership/PrivateMembership";
import PurchaseConfirmation from "../screens/membership/PurchaseConfirmation";
import Notifications from "../screens/notification";
import LearnerProfile from "../screens/profile/LearnerProfile";
import SustainablePlanner from "../screens/SustainablePlanner";
import Template from "../screens/template";
import CreateCustomTemplate from "../screens/template/CreateCustomTemplate";
import TemplateDetail from "../screens/template/TemplateDetail";
import Users from "../screens/users";
import PrivateRoute from "./PrivateRoute";
// const Home = lazy(() => import("../screens/home"));
const Login = lazy(() => import("../screens/auth/login"));
const Signup = lazy(() => import("../screens/auth/signup"));
const ForgotPassword = lazy(() => import("../screens/auth/forgotpassword"));
const Verify = lazy(() => import("../screens/auth/verify"));
const SetPassword = lazy(() => import("../screens/auth/setpassword"));
const ChangePassword = lazy(
  () => import("../screens/profile/editprofile/ChangePassword"),
);
const Create = lazy(() => import("../screens/course/create"));
const Detail = lazy(() => import("../screens/course/detail"));
const MyLearning = lazy(() => import("../screens/mylearning"));
const MyTeaching = lazy(() => import("../screens/myTeaching"));
const Cart = lazy(() => import("../screens/cart"));
const Checkout = lazy(() => import("../screens/checkout"));
const Membership = lazy(() => import("../screens/membership"));
const Dashboard = lazy(() => import("../screens/dashboard"));
const Classes = lazy(() => import("../screens/classes"));
const ClassDetail = lazy(() => import("../screens/classdetail"));
const Lecture = lazy(() => import("../screens/lecture"));
const Terms = lazy(() => import("../screens/content/terms"));
const Privacy = lazy(() => import("../screens/content/privacy"));
const Cookies = lazy(() => import("../screens/content/cookies"));
const Refund = lazy(() => import("../screens/content/refund"));
const PhysicalActivityDisclaimer = lazy(
  () => import("../screens/content/physicalactivitydisclaimer"),
);
const Dei = lazy(() => import("../screens/content/dei"));
const SuccessStory = lazy(() => import("../screens/successstory"));
const Business = lazy(() => import("../screens/business"));
const EditProfile = lazy(() => import("../screens/profile/editprofile"));
const InstructorDetails = lazy(() => import("../screens/instructordetails"));
const Journey = lazy(() => import("../screens/journey"));
const Contact = lazy(() => import("../screens/contact"));
const Community = lazy(() => import("../screens/community"));
const CommunityDetails = lazy(() => import("../screens/communitydetails"));
const PostDiscussion = lazy(() => import("../screens/postdiscussion"));
const Press = lazy(() => import("../screens/press"));
const PressDetail = lazy(() => import("../screens/pressdetail"));
const Calculators = lazy(() => import("../screens/calculators"));
const Bmr = lazy(() => import("../screens/calculators/bmr"));
const Bmi = lazy(() => import("../screens/calculators/bmi"));
const Bfp = lazy(() => import("../screens/calculators/bfp"));
const Mnc = lazy(() => import("../screens/calculators/mnc"));
const Authlayout = lazy(() => import("../components/layouts/authlayout"));

const Router = () => {
  const lang = useSelector((state) => state.language.lang);
  const {} = useGetDataQuery({ lang });
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ behavior: "smooth", top: 0, left: 0 });
  }, [pathname]);

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="auth/" exact element={<Authlayout />}>
        <Route path="login" exact element={<Login />} />
        <Route path="signup" exact element={<Signup />} />
        <Route path="forgot-password" exact element={<ForgotPassword />} />
        <Route path="verify" exact element={<Verify />} />
        <Route path="set-password" exact element={<SetPassword />} />
      </Route>
      <Route
        path="/my-learning"
        element={
          <PrivateRoute allowedRoles={["learner", "both"]}>
            <MyLearning />
          </PrivateRoute>
        }
      />
      <Route
        path="/my-teaching"
        element={
          <PrivateRoute allowedRoles={["creator", "both"]}>
            <MyTeaching />
          </PrivateRoute>
        }
      />
      <Route path="/cart" element={<Cart />} />
      <Route
        path="/checkout"
        element={
          <PrivateRoute>
            <Checkout />
          </PrivateRoute>
        }
      />
      <Route path="/memberships" element={<Membership />} />
      <Route path="/memberships/private" element={<PrivateMembership />} />
      <Route
        path="/instructor-dashboard"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/course/create"
        exact
        element={
          <PrivateRoute>
            <Create />
          </PrivateRoute>
        }
      />
      <Route
        path="/course/details/:id"
        exact
        element={
          <PrivateRoute>
            <Detail />
          </PrivateRoute>
        }
      />
      <Route path="/courses-tracks" element={<Classes />} />
      <Route path="/class-detail/:id" element={<ClassDetail />} />
      <Route
        path="/lecture/:id"
        element={
          <PrivateRoute>
            <Lecture />
          </PrivateRoute>
        }
      />
      <Route path="/success-story" element={<SuccessStory />} />
      <Route path="/about" element={<Journey />} />
      <Route path="/terms-and-condition" element={<Terms />} />
      <Route path="/privacy-policy" element={<Privacy />} />
      <Route
        path="/physical-activity-disclaimer"
        element={<PhysicalActivityDisclaimer />}
      />
      <Route path="/dei" element={<Dei />} />
      <Route path="/cookies-policy" element={<Cookies />} />
      <Route path="/refund-policy" element={<Refund />} />
      <Route path="/business" element={<Business />} />
      <Route path="/contact" element={<Contact />} />
      <Route
        path="/profile"
        element={
          <PrivateRoute allowedRoles={["creator", "both"]}>
            <InstructorDetails />
          </PrivateRoute>
        }
      />
      <Route
        path="/learnerprofile"
        element={
          <PrivateRoute allowedRoles={["learner"]}>
            <LearnerProfile />
          </PrivateRoute>
        }
      />
      <Route
        path="/profile/edit"
        element={
          <PrivateRoute>
            <EditProfile />
          </PrivateRoute>
        }
      />
      <Route
        path="/change-password"
        element={
          <PrivateRoute>
            <ChangePassword />
          </PrivateRoute>
        }
      />
      <Route path="/instructors" element={<Users />} />
      <Route path="/instructor/:id" element={<InstructorDetails />} />
      <Route
        path="/instructors/followers/:id"
        element={<InstructorFollower />}
      />
      <Route path="/instructors/review/:id" element={<InstructorReview />} />
      <Route path="/learner/:id" element={<LearnerDetails />} />
      <Route path="/press" element={<Press />} />
      <Route path="/press/:id" element={<PressDetail />} />
      <Route path="/community" element={<Community />} />
      <Route path="/community/:id" element={<CommunityDetails />} />
      <Route path="/fitness-calculators" element={<Calculators />} />
      <Route path="/fitness-calculators/bmi" element={<Bmi />} />
      <Route path="/fitness-calculators/bmr" element={<Bmr />} />
      <Route path="/fitness-calculators/macro-nutrients" element={<Mnc />} />
      <Route path="/fitness-calculators/body-fat-percentage" element={<Bfp />} />
      <Route path="/fitness-calculators/heart-rate" element={<HeartRateCalculator />} />
      <Route
        path="/fitness-calculators/ideal-body-weight"
        element={<IdealBodyWeightCalculator />}
      />
      <Route
        path="/fitness-calculators/waist-to-hip-ratio"
        element={<WaistToHipRatio />}
      />
      <Route path="/fitness-calculators/hydration" element={<Hydration />} />
      <Route path="/fitness-calculators/one-rep-max" element={<OneRepMax />} />
      <Route path="/fitness-calculators/sleep" element={<Sleep />} />
      <Route path="/fitness-tests" element={<FitnessTest />} />
      <Route path="/fitness-tests/cardio" element={<CardioFitnessTest />} />
      <Route path="/fitness-tests/strength" element={<StrengthTest />} />
      <Route path="/fitness-tests/flexibility" element={<FlexibilityTest />} />
      <Route path="/fitness-tests/balance" element={<BalanceTest />} />
      <Route
        path="/community/post"
        element={
          <PrivateRoute>
            <PostDiscussion />
          </PrivateRoute>
        }
      />
      <Route
        path="/community/edit/:id"
        element={
          <PrivateRoute>
            <CommunityEdit />
          </PrivateRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <PrivateRoute>
            <Notifications />
          </PrivateRoute>
        }
      />
      <Route
        path="/create-custom-template"
        element={
          <PrivateRoute>
            <CreateCustomTemplate />
          </PrivateRoute>
        }
      />
      <Route path="/template" element={<Template />} />
      <Route path="/templates/:slug" element={<TemplateDetail />} />

      <Route path="/success-membership" element={<PurchaseConfirmation />} />
      <Route path="/success-pss" element={<PurchaseConfirmation />} />
      <Route path="/sustainable-planner" element={<SustainablePlanner />} />

      <Route path="*" element={<Error />} />
    </Routes>
  );
};

export default Router;
